import { useEffect, useState } from "react";
import { Loader2, Check } from "lucide-react";
import { dictionary, Language } from "../../utils/dictionary";

const STEPS = [
  { key: "analysis-step-1", threshold: 15 },
  { key: "analysis-step-2", threshold: 35 },
  { key: "analysis-step-3", threshold: 55 },
  { key: "analysis-step-4", threshold: 75 },
  { key: "analysis-step-5", threshold: 90 },
  { key: "analysis-step-6", threshold: 100 },
];

// Simulated progress curve — ~8 seconds total
const PROGRESS_CURVE = [10, 25, 40, 55, 70, 82, 93, 100];
const TICK_MS = 800;

interface AnalysisLoadingProps {
  language: Language;
  /** When true, the progress animation runs to 100. When the backend responds, the
   * parent unmounts this component (switching to the result view). */
  running: boolean;
}

export const AnalysisLoading = ({ language, running }: AnalysisLoadingProps) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!running) return;
    let idx = 0;
    setProgress(0);
    const id = setInterval(() => {
      if (idx >= PROGRESS_CURVE.length) {
        clearInterval(id);
        return;
      }
      setProgress(PROGRESS_CURVE[idx]);
      idx += 1;
    }, TICK_MS);
    return () => clearInterval(id);
  }, [running]);

  const currentStepIndex = Math.max(
    0,
    STEPS.findIndex((s) => progress <= s.threshold),
  );

  return (
    <div className="flex flex-col items-center justify-center py-12 px-6">
      <div className="w-16 h-16 rounded-2xl bg-fluxi/10 flex items-center justify-center mb-4">
        <Loader2 className="w-8 h-8 text-fluxi animate-spin" />
      </div>
      <h2 className="text-lg font-semibold text-gray-800 mb-1">
        {dictionary["analysis-loading-title"]?.[language] ?? "Analizando anuncio..."}
      </h2>
      <p className="text-sm text-gray-500 mb-6 text-center">
        {dictionary[STEPS[currentStepIndex].key]?.[language] ?? ""}
      </p>

      <div className="w-full max-w-md mb-6">
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-fluxi transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <ol className="w-full max-w-md space-y-2">
        {STEPS.map((step, i) => {
          const done = i < currentStepIndex;
          const active = i === currentStepIndex;
          return (
            <li key={step.key} className="flex items-center gap-3 text-sm">
              <span
                className={`flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0 ${
                  done
                    ? "bg-emerald-500 text-white"
                    : active
                      ? "bg-fluxi text-white"
                      : "bg-gray-200 text-gray-400"
                }`}
              >
                {done ? <Check className="w-3 h-3" /> : <span className="text-[10px] font-bold">{i + 1}</span>}
              </span>
              <span className={done ? "text-gray-400 line-through" : active ? "text-gray-800 font-medium" : "text-gray-500"}>
                {dictionary[step.key]?.[language] ?? step.key}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default AnalysisLoading;
